import { useRef, useState } from "react";
import {
  ArrowLeft,
  Mail,
  Phone,
  MapPin,
  Send,
  Building2,
  User,
  MessageSquare,
  GraduationCap,
} from "lucide-react";
import { postFeedback } from "../../network/APIs/feedback";
import SuccessModal from "./SuccessModel";

const ContactPage = () => {
  const [feedbackBtn, setFeedbackBtn] = useState("Send Message");
  const [isLoading, setIsLoading] = useState(false);
  const [showPopup, setShowPopup] = useState(false);
  const [nameError, setNameError] = useState("");
  const [messageError, setMessageError] = useState("");

  // const [formData, setFormData] = useState({
  //   name: "",
  //   email: "",
  //   // subject: "General Inquiry",
  //   message: "",
  // });

  const nameRef = useRef();
  const emailRef = useRef();
  const subjectRef = useRef();
  const feedbackRef = useRef();
  const feedbackFormRef = useRef();

  const feedbackFormHandler = (e) => {
    e.preventDefault();

    const name = nameRef.current.value || "";
    const email = emailRef.current.value || "";
    const feedback = feedbackRef.current.value || "";
    const subject = subjectRef.current.value || "";

    // Validate name field
    const hasHtmlTags = /<[a-zA-Z][^>]*>|<\/[a-zA-Z][^>]*>|javascript:|on\w+\s*=/.test(name);
    const hasSpecialChars = /[^a-zA-Z\s]/.test(name);
    if (hasHtmlTags || hasSpecialChars) {
      setNameError("Name can only contain letters and spaces. Special characters and HTML tags are not allowed.");
      return;
    }
    setNameError("");

    // Validate message field
    const messageHasHtmlTags = /<[a-zA-Z][^>]*>|<\/[a-zA-Z][^>]*>|javascript:|on\w+\s*=/.test(feedback);
    if (messageHasHtmlTags) {
      setMessageError("Message cannot contain HTML tags or scripts.");
      return;
    }
    setMessageError("");

    const prepareData = {
      name: name,
      email: email,
      feedback: feedback,
      subject: subject,
    };
    // console.log("prepareData", prepareData);

    if (!isLoading) {
      setIsLoading(true);
      postFeedback(prepareData)
        .then((response) => {
          setIsLoading(false);
          if (response.success) {
            feedbackFormRef.current.reset();
            setShowPopup(true);
            setTimeout(() => setShowPopup(false), 4000);
            setFeedbackBtn("Response submitted!");
            setTimeout(() => {
              setFeedbackBtn("Send Message");
            }, 3000);
          }
        })
        .catch(() => {
          setIsLoading(false);
        });
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 font-sans text-slate-900">
      {/* Thank You Popup */}
      <SuccessModal
        show={showPopup}
        onClose={() => setShowPopup(false)}
        title="Thank You!"
        message={`Thanks for reaching out!\nWe'll get back to you shortly.`}
        buttonLabel="Got it!"
      />
      {/* Navigation Header */}
      <div className="sticky top-0 z-50 bg-white/80 backdrop-blur-md border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <button
            onClick={() => window.history.back()}
            className="flex items-center gap-2 text-slate-600 hover:text-blue-600 font-bold text-sm transition-colors"
          >
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
          <div className="font-bold text-lg text-slate-900">
            Contact Support
          </div>
          <div className="w-10"></div> {/* Spacer for centering */}
        </div>
      </div>

      {/* Hero Content */}
      <section className="pt-16 pb-24 relative overflow-hidden">
        {/* Background blobs */}
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-blue-100/40 rounded-full blur-3xl -z-10 -mr-20 -mt-20"></div>
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-blue-100/30 rounded-full blur-3xl -z-10 -ml-20"></div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <h1 className="text-4xl md:text-5xl font-black text-slate-900 mb-6 tracking-tight">
              Get in <span className="text-blue-600">Touch</span>
            </h1>
            <p className="text-lg text-slate-600 leading-relaxed">
              Have questions about your job search, hiring, or college
              partnership? We're here to help you succeed.
            </p>
          </div>

          <div className="grid grid-cols-2 xsm:grid-cols-1 gap-12 items-start">
            {/* Left Column: Contact Info Cards */}
            <div className="lg:col-span-5 space-y-6">
              {/* Candidate Support */}
              <div className="bg-white p-6 rounded-3xl shadow-sm border border-slate-100 hover:border-blue-100 transition-colors group">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-12 h-12 bg-blue-50 text-blue-600 rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform">
                    <User className="w-6 h-6" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-slate-900">
                      Job Seekers
                    </h3>
                    <p className="text-sm text-slate-500">
                      For login issues & application updates
                    </p>
                  </div>
                </div>
                <a
                  href="mailto:contact@freshmentapp.com"
                  className="flex items-center gap-2 text-slate-700 font-bold hover:text-blue-600 transition"
                >
                  <Mail className="w-4 h-4" /> contact@freshmentapp.com
                </a>
              </div>

              {/* Colleges Section (NEW) */}
              <div className="bg-white p-6 rounded-3xl shadow-sm border border-slate-100 hover:border-[#FF5F14]/30 transition-colors group">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-12 h-12 bg-orange-50 text-[#FF5F14] rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform">
                    <GraduationCap className="w-6 h-6" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-slate-900">
                      Colleges / TPO
                    </h3>
                    <p className="text-sm text-slate-500">
                      For campus placements & partnerships
                    </p>
                  </div>
                </div>
                <a
                  href="mailto:contact@freshmentapp.com"
                  className="flex items-center gap-2 text-slate-700 font-bold hover:text-[#FF5F14] transition"
                >
                  <Mail className="w-4 h-4" /> contact@freshmentapp.com
                </a>
              </div>

              {/* Employer Sales */}
              <div className="bg-white p-6 rounded-3xl shadow-sm border border-slate-100 hover:border-blue-100 transition-colors group">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-12 h-12 bg-indigo-50 text-indigo-600 rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform">
                    <Building2 className="w-6 h-6" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-slate-900">
                      Employers
                    </h3>
                    <p className="text-sm text-slate-500">
                      Hiring solutions & corporate tie-ups
                    </p>
                  </div>
                </div>
                <div className="flex flex-wrap gap-x-6 gap-y-2">
                  <a
                    href="mailto:hiring@freshmentapp.com"
                    className="flex items-center gap-2 text-slate-700 font-bold hover:text-blue-600 transition"
                  >
                    <Mail className="w-4 h-4" /> onboarding@freshmentapp.com
                  </a>
                  {/* <a
                    href="tel:+919876543210"
                    className="flex items-center gap-2 text-slate-700 font-bold hover:text-blue-600 transition"
                  >
                    <Phone className="w-4 h-4" /> +91 98765 43210
                  </a> */}
                </div>
              </div>

              {/* General/Office */}
              <div className="bg-white p-6 rounded-3xl shadow-sm border border-slate-100 hover:border-blue-100 transition-colors group">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-12 h-12 bg-emerald-50 text-emerald-600 rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform">
                    <MapPin className="w-6 h-6" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-slate-900">
                      Headquarters
                    </h3>
                    <p className="text-sm text-slate-500">
                      Visit us or send post
                    </p>
                  </div>
                </div>
                <address className="not-italic text-sm text-slate-600 leading-relaxed pl-1">
                  GradConnect Innovations Pvt Ltd
                  <br />
                  B-519, RK Iconic Sheetal Park,
                  <br />
                  Rajkot- 360006, Gujarat, India
                </address>
              </div>
            </div>

            {/* Right Column: Contact Form */}
            <div className="lg:col-span-7">
              <div className="bg-white rounded-[2.5rem] p-8 md:p-10 shadow-xl shadow-slate-200/50 border border-slate-100 relative overflow-hidden">
                <div className="absolute top-0 right-0 w-32 h-32 bg-blue-50 rounded-bl-[4rem] -z-0"></div>

                <h2 className="text-2xl font-black text-slate-900 mb-8 relative z-10 flex items-center gap-3">
                  <MessageSquare className="w-6 h-6 text-blue-600" />
                  Send us a Message
                </h2>

                <form
                  // onSubmit={() => handleSubmit()}
                  ref={feedbackFormRef}
                  onSubmit={feedbackFormHandler}
                  className="space-y-6 relative z-10"
                >
                  <div className="grid grid-cols-2 sm:grid-cols-1 gap-6">
                    <div className="space-y-2">
                      <label className="text-xs font-bold text-slate-500 uppercase tracking-wider ml-1">
                        Your Name
                      </label>
                      <input
                        type="text"
                        required
                        name="name"
                        autoComplete="off"
                        minLength={3}
                        ref={nameRef}
                        onChange={(e) => {
                          const value = e.target.value;
                          const hasHtmlTags = /<[a-zA-Z][^>]*>|<\/[a-zA-Z][^>]*>|javascript:|on\w+\s*=/.test(value);
                          const hasSpecialChars = /[^a-zA-Z\s]/.test(value);
                          if (hasHtmlTags || hasSpecialChars) {
                            setNameError("Name can only contain letters and spaces. Special characters and HTML tags are not allowed.");
                          } else {
                            setNameError("");
                          }
                        }}
                        className={`w-full p-4 bg-slate-50 border rounded-xl text-slate-900 font-medium focus:outline-none focus:ring-2 transition-all ${nameError ? "border-red-500 focus:ring-red-500/20 focus:border-red-500" : "border-slate-200 focus:ring-blue-500/20 focus:border-blue-500"}`}
                        placeholder="John Doe"
                      // value={formData.name}
                      // onChange={(e) =>
                      //   setFormData({ ...formData, name: e.target.value })
                      // }
                      />
                      {nameError && (
                        <p className="text-xs text-red-500 font-medium">{nameError}</p>
                      )}
                    </div>
                    <div className="space-y-2">
                      <label className="text-xs font-bold text-slate-500 uppercase tracking-wider ml-1">
                        Email Address
                      </label>
                      <input
                        type="email"
                        required
                        name="email"
                        autoComplete="off"
                        minLength={3}
                        ref={emailRef}
                        className="w-full p-4 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 font-medium focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all"
                        placeholder="john@example.com"
                      // value={formData.email}
                      // onChange={(e) =>
                      //   setFormData({ ...formData, email: e.target.value })
                      // }
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className="text-xs font-bold text-slate-500 uppercase tracking-wider ml-1">
                      Subject
                    </label>
                    <select
                      name="subject"
                      ref={subjectRef}
                      className="w-full p-4 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 font-medium focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all appearance-none"
                      // value={formData.subject}
                      onChange={(e) => { }
                        // setFormData({ ...formData, subject: e.target.value })
                      }
                    >
                      <option>General Inquiry</option>
                      <option>Job Application Issue</option>
                      <option>Employer / Hiring Support</option>
                      <option>College Partnership</option>
                      <option>Report a Bug</option>
                    </select>
                  </div>

                  <div className="space-y-2">
                    <label className="text-xs font-bold text-slate-500 uppercase tracking-wider ml-1">
                      Message
                    </label>
                    <textarea
                      name="feedback"
                      rows={4}
                      autoComplete="off"
                      required
                      minLength={3}
                      ref={feedbackRef}
                      onChange={(e) => {
                        const value = e.target.value;
                        const hasHtmlTags = /<[a-zA-Z][^>]*>|<\/[a-zA-Z][^>]*>|javascript:|on\w+\s*=/.test(value);
                        if (hasHtmlTags) {
                          setMessageError("Message cannot contain HTML tags or scripts.");
                        } else {
                          setMessageError("");
                        }
                      }}
                      className={`w-full p-4 bg-slate-50 border rounded-xl text-slate-900 font-medium focus:outline-none focus:ring-2 transition-all resize-none ${messageError ? "border-red-500 focus:ring-red-500/20 focus:border-red-500" : "border-slate-200 focus:ring-blue-500/20 focus:border-blue-500"}`}
                      placeholder="How can we help you?"
                    // value={formData.message}
                    // onChange={(e) =>
                    //   setFormData({ ...formData, message: e.target.value })
                    // }
                    ></textarea>
                    {messageError && (
                      <p className="text-xs text-red-500 font-medium">{messageError}</p>
                    )}
                  </div>

                  <button className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-4 rounded-xl shadow-lg shadow-blue-500/30 transition-all transform active:scale-[0.98] flex items-center justify-center gap-2">
                    <Send className="w-5 h-5" /> {feedbackBtn}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
